// noinspection JSUnusedGlobalSymbols

import {
    inject,
    Injectable,
    makeStateKey,
    PLATFORM_ID,
    REQUEST,
    TransferState,
} from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action, Store } from "@ngrx/store";
import { catchError, concat, filter, Observable, of, switchMap } from "rxjs";
import { HttpEvent } from "@angular/common/http";
import * as UserActions from "../actions/user.actions";
import { logout } from "../actions/user.actions";
import { isPlatformBrowser, isPlatformServer } from "@angular/common";
import { Request } from "express";
import { jwtDecode, JwtPayload } from "jwt-decode";
import { CookieService } from "ngx-cookie-service";
import { RootState } from "../reducers/root.reducer";
import { ApiHttpService } from "../services/api.service";
import { UserSession } from "@home-shark/types";
import { openInfo, userLoadedInApp } from "../actions/app.actions";
import { MatDialog } from "@angular/material/dialog";
import { openLoginDialog } from "../modules/overview/lib/dialogs.lib";
import { Router } from "@angular/router";

const USER_STATE = makeStateKey<UserSession>("user"); // Define a key

// noinspection JSUnusedGlobalSymbols
@Injectable()
export class UserEffects {
    private platformId = inject(PLATFORM_ID);
    private request = inject<Request>(REQUEST, { optional: true });
    public actions$ = inject(Actions);
    public transferState = inject(TransferState);
    private readonly dialog = inject(MatDialog);

    constructor(
        public store: Store<RootState>,
        public api: ApiHttpService,
        public cookieService: CookieService,
        public router: Router
    ) {}

    isJWTExpired(token: JwtPayload): boolean {
        if (!token || !token.exp) return true; // Treat missing exp as expired

        const currentTime = Math.floor(Date.now() / 1000); // Convert to seconds
        return token.exp < currentTime;
    }

    private loadOrExpireUserWithJWT(jwtToken: string) {
        if (jwtToken) {
            const user = jwtDecode<JwtPayload & UserSession>(jwtToken) as any;
            if (this.isJWTExpired(user)) {
                this.transferState.remove(USER_STATE);
                return concat(
                    of(UserActions.logout({})),
                    of(userLoadedInApp())
                );
            }
            this.transferState.set(USER_STATE, user);
            return concat(
                of(
                    UserActions.userLoaded({
                        user: {
                            id: user.id,
                            email: user.email,
                            firstName: user.firstName,
                            lastName: user.lastName,
                            validated: user.validated,
                        },
                    })
                )
            );
        }
        return null;
    }

    loadUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.loadUser), // Triggered when loadUser action is dispatched
            switchMap(() => {
                if (!isPlatformServer(this.platformId)) {
                    if (!this.cookieService?.get("access_token")) {
                        return of(userLoadedInApp());
                    }
                    return this.api.get("/users/self").pipe(
                        switchMap((res: any) => {
                            return concat(
                                res?.id
                                    ? of(
                                          UserActions.setUser({
                                              user: res as UserSession,
                                          })
                                      )
                                    : of(UserActions.failedToLoadUser()),
                                of(userLoadedInApp())
                            );
                        })
                    );

                    // // Load from transfer state
                    // const cachedUser = this.transferState.get(USER_STATE, null)
                    // if (cachedUser) {
                    //     return concat(
                    //         of(UserActions.setUser({ user: cachedUser })),
                    //         of(setUserLoadedInApp())
                    //     )
                    // }
                    // // Load form Local Storage
                    // try {
                    //     const userStr = localStorage.getItem("user")
                    //     if (userStr) {
                    //         const user: UserSession = JSON.parse(userStr)
                    //         if (user.id) {
                    //             return concat(
                    //                 of(UserActions.setUser({ user })),
                    //                 of(setUserLoadedInApp())
                    //             )
                    //         }
                    //     }
                    // } catch (e) {
                    //     //TODO: Log creation -> Add Error Log
                    //     console.error("Failed to load user from client storage")
                    // }
                    //
                    // const res = this.loadOrExpireUserWithJWT(
                    //     this.cookieService?.get("access_token")
                    // )
                    // if (res) return res
                } else {
                    // TODO: Load user from load from jwt token from cookie and set the transferstate
                    if (this.request) {
                        const res = this.loadOrExpireUserWithJWT(
                            this.request?.cookies
                                ? this.request?.cookies["access_token"]
                                : undefined
                        );
                        if (res) return res;
                    }
                    // if (!this.request?.cookies?.["access_token"]) {
                    //     return of(null)
                    // }
                    // return this.api.get("/users/self").pipe(
                    //     switchMap((res: any) => {
                    //         return of(
                    //             res?.id
                    //                 ? UserActions.setUser({
                    //                       user: res as UserSession,
                    //                   })
                    //                 : UserActions.failedToLoadUser()
                    //         )
                    //     })
                    // )
                }
                return of(userLoadedInApp());
            }),
            filter((res) => !!res)
        )
    );

    login$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.login), // Triggered when loadUser action is dispatched
            switchMap((action: ReturnType<typeof UserActions.login>) => {
                if (!isPlatformServer(this.platformId)) {
                    return this.api
                        .post<UserSession>("/users/login", {
                            email: action.email,
                            password: action.password,
                        })
                        .pipe(
                            catchError((err) => {
                                let infoData = {
                                    data: {
                                        title: "Failed to Login",
                                        text: "Something went wrong. Try again later.",
                                    },
                                    width: "20rem",
                                    height: "11rem",
                                };
                                if (err.status === 404) {
                                    infoData.data.text = "Account Not Found";
                                } else if (err.status === 409) {
                                    infoData.data.text =
                                        "Check email for confirmation";
                                }
                                return of(openInfo(infoData));
                            }),
                            switchMap((res) => {
                                // If error is thrown then pass on the action
                                if ((res as any)?.type === "[App] Open Info") {
                                    return of(res);
                                }
                                const userRes: any = res;
                                const user: UserSession = {
                                    id: userRes.id,
                                    email: userRes.email,
                                    lastName: userRes.lastName,
                                    firstName: userRes.firstName,
                                    validated: userRes.validated || false,
                                };

                                if (action?.closeFn) action.closeFn();
                                if (
                                    !action.ignoreRefresh &&
                                    isPlatformBrowser(this.platformId)
                                ) {
                                    const timeout = setTimeout(() => {
                                        window.location.reload();
                                        clearTimeout(timeout);
                                    }, 1);
                                }
                                return of(UserActions.setUser({ user }));
                            })
                        );
                }
                return of(null as any);
            }),
            filter((action) => !!action)
        )
    );

    editUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.editUser),
            switchMap((action) =>
                this.api.put("/users/self", action.user).pipe(
                    switchMap((res) => {
                        if (typeof action.onSuccess === "function")
                            action.onSuccess();

                        let infoData = {
                            data: {
                                title: "Success!",
                                text: "Your data has been updated",
                            },
                            width: "22rem",
                            height: "13rem",
                        };
                        return concat(
                            of(openInfo(infoData)),
                            of(UserActions.userEdited(action))
                        );
                    })
                )
            )
        )
    );

    requestPasswordReset$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.requestPasswordReset), // Triggered when loadUser action is dispatched
            switchMap(
                (
                    action: ReturnType<typeof UserActions.requestPasswordReset>
                ) => {
                    if (!isPlatformServer(this.platformId)) {
                        return this.api
                            .put<UserSession>("/users/password-reset-request", {
                                email: action.email,
                            })
                            .pipe(
                                catchError((err) => {
                                    return null;
                                }),
                                switchMap((res) => {
                                    // If error is thrown then pass on the action

                                    let infoData = {
                                        data: {
                                            title: "Password Reset Email Sent",
                                            text: "A password reset email has been sent. Please check your inbox for instructions to reset your password.",
                                        },
                                        width: "22rem",
                                        height: "13rem",
                                    };
                                    if (action.closeFn) action.closeFn();
                                    if (action.enableBtn) action.enableBtn();
                                    return concat(of(openInfo(infoData)));
                                })
                            );
                    }
                    return of(null as any);
                }
            ),
            filter((action) => !!action)
        )
    );

    confirm$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.confirm), // Triggered when loadUser action is dispatched
            switchMap((action: ReturnType<typeof UserActions.confirm>) => {
                if (!isPlatformServer(this.platformId)) {
                    return this.api
                        .put<{
                            existingCode: string;
                        }>("/users/confirm", { code: action.code })
                        .pipe(
                            catchError((err) => {
                                let infoData = {
                                    data: {
                                        title: "Failed to Confirm User",
                                        text: "Something went wrong. Try again later.",
                                    },
                                    width: "20rem",
                                    height: "11rem",
                                };
                                if (err?.status === 404) {
                                    infoData.data.text =
                                        "Invalid confirmation code";
                                } else if (err?.status === 401) {
                                    openLoginDialog(this.dialog);
                                    return of(logout({}));
                                }
                                return of(openInfo(infoData));
                            }),
                            switchMap(
                                (
                                    res:
                                        | HttpEvent<{ existingCode: string }>
                                        | ReturnType<typeof openInfo>
                                        | ReturnType<typeof logout>
                                ) => {
                                    // If error is thrown then pass on the action

                                    if (res.type) return of(res);
                                    const returnData: { existingCode: string } =
                                        res as any;
                                    let infoData = {
                                        data: {
                                            title: "User Confirmed",
                                            text: "Now you should be able to login",
                                        },
                                        width: "23rem",
                                        height: "12rem",
                                    };

                                    if (returnData?.existingCode === "email") {
                                        infoData.data.title = "Phone Confirmed";
                                        infoData.data.text =
                                            "Check email for confirmation link to enable login";
                                    }
                                    // TODO: Re-enable after phone confirmation is complete
                                    //
                                    // else if (
                                    //     returnData?.existingCode === "phone"
                                    // ) {
                                    //     infoData.data.title = "Email Confirmed"
                                    //     infoData.data.text =
                                    //         "Check phone text messages for confirmation link to enable login"
                                    // }

                                    return of(openInfo(infoData));
                                }
                            )
                        );
                }
                return of(null as any);
            }),
            filter((action) => !!action)
        )
    );

    passwordReset$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.passwordReset), // Triggered when loadUser action is dispatched
            switchMap(
                (action: ReturnType<typeof UserActions.passwordReset>) => {
                    if (!isPlatformServer(this.platformId)) {
                        return this.api
                            .put<{
                                existingCode: string;
                            }>("/users/reset-password", { code: action.code, password: action.password })
                            .pipe(
                                catchError((err) => {
                                    let infoData = {
                                        data: {
                                            title: "Failed to Reset Password",
                                            text: "Failed to reset password. Please request another password reset email and try again.",
                                        },
                                        width: "22rem",
                                        height: "13rem",
                                    };
                                    return of(openInfo(infoData));
                                }),
                                switchMap(
                                    (
                                        res:
                                            | HttpEvent<{
                                                  existingCode: string;
                                              }>
                                            | ReturnType<typeof openInfo>
                                            | ReturnType<typeof logout>
                                    ) => {
                                        if (action?.closeFn) action.closeFn();
                                        if (action?.enableBtn)
                                            action.enableBtn();

                                        if (res?.type) return of(res);

                                        let infoData = {
                                            data: {
                                                title: "Password reset",
                                                text: "Your password has be reset. try logging in again",
                                            },
                                            width: "22rem",
                                            height: "13rem",
                                        };
                                        return of(openInfo(infoData));
                                    }
                                )
                            );
                    }
                    return of(null as any);
                }
            ),
            filter((action) => !!action)
        )
    );

    logout$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.logout), // Triggered when loadUser action is dispatched
            switchMap((action) => {
                if (!isPlatformServer(this.platformId)) {
                    return this.api.delete("/users/logout").pipe(
                        catchError((err) => {
                            console.error(err);
                            return of(null);
                        }),
                        switchMap((res) => {
                            this.cookieService?.delete("access_token");
                            if (action?.refresh) window.location.reload();
                            return of(
                                UserActions.setUser({
                                    user: {
                                        email: "",
                                        firstName: "",
                                        lastName: "",
                                        id: "",
                                    },
                                })
                            );
                        })
                    );
                }
                return of(null as any);
            }),
            filter((action) => !!action)
        )
    );

    signup$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.signup), // Triggered when loadUser action is dispatched
            switchMap((action) => {
                if (!isPlatformServer(this.platformId)) {
                    return this.api
                        .post("/users/signup", {
                            firstName: action.firstName,
                            lastName: action.lastName,
                            email: action.email,
                            username: action.email,
                            password: action.password,
                            phone: action.phone,
                        })
                        .pipe(
                            catchError((err) => {
                                let infoData = {
                                    data: {
                                        title: "Failed to Sign Up",
                                        text: "Something went wrong. Try again later.",
                                    },
                                    width: "20rem",
                                    height: "11rem",
                                };
                                if (err.status === 409) {
                                    if (err.error === "Email already exists") {
                                        // User needs to confirm email or phone
                                        infoData.data.text =
                                            "Email already exists";
                                    } else if (
                                        err.error === "Phone already exists"
                                    ) {
                                        // Something unknown happened
                                        infoData.data.text =
                                            "Phone already exists";
                                    }
                                }
                                if (err.status === 400) {
                                    if (err.error === "Invalid password") {
                                        // User needs to confirm email or phone
                                        infoData.data.text = "Invalid password";
                                    }
                                }
                                return of(openInfo(infoData));
                            }),
                            switchMap((res) => {
                                // If error is thrown then pass on the action
                                if ((res as any)?.type === "[App] Open Info") {
                                    return of(res);
                                }
                                this.cookieService?.delete("access_token");
                                return of(
                                    UserActions.signedUp({
                                        email: action.email,
                                    })
                                );
                            })
                        );
                }
                return of(null as any);
            }),
            filter((action) => !!action)
        )
    );

    // @ts-ignore
    getSelf$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.getSelf), // Triggered when loadUser action is dispatched
            switchMap(() => {
                if (!isPlatformServer(this.platformId)) {
                    return this.api.get("/users/self").pipe(
                        catchError((err) => {
                            console.error(err);
                            if (err?.status === 401) {
                                return of(logout({}));
                            }
                            return of(null);
                        }),
                        filter((action) => !!action),
                        switchMap((res) => {
                            console.log("Successfully got self");
                            console.dir(res);
                            return of(null);
                        })
                    );
                }
                return of(null) as any as Observable<Action<string>>;
            }),
            filter((action) => !!action)
        )
    );

    manageSubscription = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.manageSubscription), // Triggered when loadUser action is dispatched
            switchMap((action) => {
                return this.api.post("/users/manage-subscription", {}, {}).pipe(
                    catchError(async (err) => {
                        return null;
                    }),
                    switchMap((session: any) => {
                        if (session?.url) {
                            location.assign(session.url);
                        } else {
                            this.router.navigate(["subscribe"]);
                        }
                        return of(null as Action);
                    })
                );
            }),
            filter((action) => !!action)
        )
    );
}
